import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import { useCurrentPath } from '@/components/Nav/utilities';
import OverflowDots from '../../../public/assets/icons/overflow-horizontal.svg';
import { Route } from 'next';

const DoubleDeckerMenu: React.FC<{
  menuItems: { name: string; href: Route }[];
}> = ({ menuItems }) => {
  const currentPath = useCurrentPath();

  const itemRef = useRef<HTMLElement[]>([]);
  const [visibilityMatrix, setVisibilityMatrix] = useState<number[]>(
    new Array(menuItems.length).fill(1)
  );

  useEffect(() => {
    const observerCb: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.target instanceof HTMLElement) {
          const itemIndex = Number(entry.target.dataset.id);
          if (entry.isIntersecting) {
            setVisibilityMatrix((prev) => {
              const newMatrix = [...prev];
              newMatrix[itemIndex] = 1;
              return newMatrix;
            });
          } else {
            setVisibilityMatrix((prev) => {
              const newMatrix = [...prev];
              newMatrix[itemIndex] = 0;
              return newMatrix;
            });
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCb, {
      root: null,
      threshold: 1,
      rootMargin: '100% -50px 100% 0px',
    });

    const nodes = itemRef.current;

    nodes.forEach((node) => {
      observer.observe(node);
    });

    return () => {
      nodes.forEach((node) => {
        observer.unobserve(node);
      });
    };
  }, []);

  const visibleCount = visibilityMatrix.reduce((prev, curr) => prev + curr, 0);
  const hiddenCount = menuItems.length - visibleCount;
  const hiddenItems = menuItems.slice(menuItems.length - hiddenCount);

  return (
    <nav className="relative mx-auto hidden h-16 max-w-view gap-x-4 text-sm md:flex md:px-4">
      <ul className="flex grow flex-nowrap overflow-hidden">
        {menuItems.map(({ name, href }, index) => (
          <li
            key={name}
            className={classNames(index >= visibleCount && 'invisible')}
            ref={(el) => {
              if (el) itemRef.current[index] = el;
            }}
            data-id={index}
          >
            <Link
              className="relative flex h-full items-center text-nowrap px-4 py-2 transition-colors hover:bg-[#E8E8E8]"
              href={href}
            >
              {name}
              <span
                className={classNames([
                  'absolute bottom-0 left-0 right-0 h-[3px] bg-primary-default transition',
                  href !== currentPath?.href && 'invisible opacity-0',
                ])}
              />
            </Link>
          </li>
        ))}
      </ul>
      {hiddenCount > 0 && (
        <div className="group/item relative flex h-full cursor-default items-center text-nowrap border-b-2 px-4 py-2 transition-colors hover:bg-[#E8E8E8]">
          More <OverflowDots className="ml-[0.375rem]" />
          <ul className="invisible absolute right-0 top-[70%] mt-2 min-w-[21rem] rounded border border-gray-200 bg-white shadow-lg transition-all duration-300 group-hover/item:visible group-hover:opacity-100">
            {hiddenItems.map(({ name, href }) => (
              <li
                key={name}
                className={classNames([
                  'w-full text-sm hover:bg-[#E8E8E8]',
                  href === currentPath?.href &&
                    'bg-background-3 text-[#161616]',
                ])}
              >
                <Link
                  className="block h-full text-nowrap px-6 py-2.5 transition-colors hover:bg-[#E8E8E8]"
                  href={href}
                >
                  {name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="absolute bottom-0 left-4 right-4 -z-10 hidden h-px bg-background-3 md:block" />
    </nav>
  );
};

export default DoubleDeckerMenu;
